import { useState } from 'react';
import { UIImageUploadDialog } from '@/components/dialogs/ui-image-upload-dialog';
import type { UIImageCategory } from '@/modules/uiImages/types';
import { Overline } from '@/components/ui/text';
import type { CoverEntry, CoverSavePayload } from './usePublishFlowImages';
import { ImageButton } from '@/components/ui/image-button';
import { shownUrl } from '@/utils/localCopies';

interface Props {
  aspect: '9/16' | '16/9' | '1/1';
  label: string;
  category: UIImageCategory;
  entry: CoverEntry;
  displayUrl: string | null;
  caption?: string;
  fullWidth?: boolean;
  heightRem?: number;
  heightPx?: number;
  error?: boolean;
  // Shows a spinner over the tile and blocks opening the upload dialog, for
  // while a generated image is on its way.
  busy?: boolean;
  projectInstanceId?: string | null;
  sceneOriginId?: string | null;
  onSave: (payload: CoverSavePayload) => Promise<void>;
  onRemove: () => Promise<void>;
}

export function CoverImageTile({
  aspect,
  label,
  category,
  entry,
  displayUrl,
  caption,
  fullWidth = false,
  heightRem = 9,
  heightPx,
  error = false,
  busy = false,
  projectInstanceId,
  sceneOriginId,
  onSave,
  onRemove,
}: Props) {
  const [open, setOpen] = useState(false);
  const isProjectCategory = category === 'ProjectPortrait' || category === 'ProjectLandscape';

  return (
    <div className="space-y-2">
      <ImageButton
        framed
        label={displayUrl ? `Change ${label}` : `Upload ${label}`}
        invalid={error}
        busy={busy}
        onClick={() => !busy && setOpen(true)}
        className={fullWidth ? 'w-full' : undefined}
        style={
          fullWidth
            ? { aspectRatio: aspect }
            : heightPx != null
              ? { aspectRatio: aspect, height: `${heightPx}px` }
              : { aspectRatio: aspect, height: `${heightRem}rem` }
        }
      >
        {displayUrl ? <img src={shownUrl(displayUrl)} alt={label} className="absolute inset-0 w-full h-full object-cover" /> : undefined}
      </ImageButton>

      <div className="flex items-center justify-between gap-2">
        <Overline size="group">{label}</Overline>
        {caption && <p className="font-lureo-font-ui text-lureo-11 text-lureo-brand-ink leading-[1.3]">{caption}</p>}
      </div>

      <UIImageUploadDialog
        open={open}
        onOpenChange={setOpen}
        imageCategory={category}
        projectInstanceId={isProjectCategory ? projectInstanceId ?? undefined : undefined}
        sceneOriginId={isProjectCategory ? undefined : sceneOriginId ?? undefined}
        currentImage={entry.url ?? undefined}
        currentOriginalImage={entry.originalUrl ?? undefined}
        closeOnRemove
        onImageSelect={(url) => {
          if (!url) void onRemove().catch((e) => console.error('[cover-tile] remove failed', e));
          else void onSave({ imageOriginalUrl: url }).catch((e) => console.error('[cover-tile] save failed', e));
        }}
        onCropApplied={(data) =>
          void onSave({
            imageOriginalUrl: data.imageOriginalUrl,
            imageCroppedUrl: data.imageCroppedUrl,
            crop: data.crop,
          }).catch((e) => console.error('[cover-tile] save failed', e))
        }
      />
    </div>
  );
}
